/**
 * The dialog for moving a dispensing job on, where there is something to check first:
 *
 * - marking the order sent: what was ordered, from whom and by when, and the supplier's reference.
 *   CatTrax doesn't send it - the practice emails or uploads the order PDF to the lab itself;
 * - marking it collected (or delivered) while something is still owing.
 *
 * An unpaid amount - what was due when the order is sent (the 50% deposit, or payment in full), or the
 * balance at collection - is a reminder, with the payment form beside it. It never stops the job moving
 * on: the patient may pay later, or have paid somewhere else.
 *
 * Opened from the dashboard's dispensing tile and the practice job list. Resolves to { reference, job }:
 * the supplier's reference (or null), and the job as it is after any payment taken here.
 */
app.service("LensStatusDialog", [
	"$mdDialog",
	"LensDispensingStore",
	function ($mdDialog, Store) {
		return {
			// Whether this step needs the dialog: always to send the order, and to collect only while
			// something is owing. The other steps move on with a click.
			needed: (job, action) => !!action && (action.next === "ORDERED" || (action.next === "COLLECTED" && Store.owing(job) > 0)),

			/**
			 * @param {Object} job
			 * @param {Object} patient - { id, firstName, lastName }, named on the order.
			 * @param {Object} action - the job's next action (LensDispensingStore.nextActionFor).
			 * @param {boolean} [multiple] - Whether it opens over another dialog, as the job list is.
			 */
			show: function (job, patient, action, multiple) {
				return $mdDialog.show({
					controller: "lensStatusModalController",
					templateUrl: "app/src/js/components/lensDispensing/lens-status-modal.tmpl.html",
					parent: angular.element(document.body),
					clickOutsideToClose: false,
					escapeToClose: true,
					multiple: !!multiple,
					locals: { job: job, patient: patient || {}, action: action },
				});
			},
		};
	},
]);

app.controller("lensStatusModalController", [
	"$scope",
	"$mdDialog",
	"LensDispensingStore",
	"LensPrescribingStore",
	"job",
	"patient",
	"action",
	function ($scope, $mdDialog, Store, Rx, job, patient, action) {
		$scope.S = Store;
		$scope.job = job;
		$scope.patient = patient;
		$scope.action = action;
		$scope.sending = action.next === "ORDERED";
		$scope.patientName = [patient.firstName, patient.lastName].filter(Boolean).join(" ") || "the patient";
		$scope.firstName = patient.firstName || "the patient";

		$scope.title = $scope.sending ? "Mark order sent" : `Mark ${action.label.toLowerCase()}`;
		$scope.contacts = Store.isContacts(job);
		$scope.kind = Store.isCustom(job) ? "Hard lens" : $scope.contacts ? "Contacts" : Rx.glassesKind(job);
		$scope.what = $scope.contacts
			? [job.product && `${job.product.brand || ""} ${job.product.name || ""}`.trim(), Store.supplyText(job)].filter(Boolean).join(" · ")
			: [Store.frameName(job.frame), job.lens && `${job.lens.supplier} ${job.lens.name}`].filter(Boolean).join(" · ");
		$scope.supplier = Store.supplierFor(job) || "the supplier";
		$scope.due = Store.addDays(new Date(), Store.leadDaysFor(job));

		// Where the job is, and where this step takes it - in its pathway's words ("Delivered" for a
		// contact lens supply sent to the patient).
		const pill = (j) => ({ label: Store.statusLabel(j), icon: Store.statusIcon(j), css: "status-" + j.status.toLowerCase().replace("_", "-") });
		$scope.from = pill(job);
		$scope.to = pill(angular.extend({}, job, { status: action.next }));

		$scope.form = { reference: "" };

		// The order PDF, to open and send before marking it sent. A job not yet invoiced has none.
		$scope.hasOrderDocument = () => Store.hasOrderDocument(job);
		$scope.openOrderDocument = function () {
			const tab = window.open("", "_blank");
			Store.orderDocument(patient.id, job).then(
				function (blob) {
					const url = URL.createObjectURL(blob);
					if (tab) tab.location.href = url;
					else window.open(url, "_blank");
					setTimeout(() => URL.revokeObjectURL(url), 60000);
				},
				() => tab && tab.close()
			);
		};

		// ------------------------------------------------------------------
		// Payment
		// ------------------------------------------------------------------
		// What should have been paid by now: what was due at ordering, or everything at collection.
		$scope.unpaid = () => ($scope.sending ? Store.dueNow($scope.job) : Store.owing($scope.job));
		$scope.unpaidLabel = function () {
			if (!$scope.sending) return "The balance";
			return $scope.job.billing && $scope.job.billing.paymentOption === "full" ? "Payment in full" : "The 50% deposit";
		};
		$scope.showPayment = !!job.invoice && $scope.unpaid() > 0;

		// A payment taken here: the dialog carries on with the job as it now is.
		$scope.paid = (saved) => ($scope.job = saved);

		$scope.confirm = () => $mdDialog.hide({ reference: ($scope.form.reference || "").trim() || null, job: $scope.job });
		$scope.cancel = () => $mdDialog.cancel();
	},
]);
